import chainHandlers from "@gef-ui/utils/chainHandlers";
import useControlled from '@material-ui/core/utils/useControlled';
import PropTypes from 'prop-types';
import not from "ramda/es/not";
import when from "ramda/es/when";
import cx from "ramda-extension/es/cx";
import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import DropdownContent from '../DropdownContent';
import { createDropdownCloseEvent } from '../utils';
const dispatchCloseOtherDropdownEvent = ()=>document.dispatchEvent(createDropdownCloseEvent());
const Dropdown = ({ children, className, toggleNode, openOnHover, popperModifiers, openOnRightClick, closeOnClick = true, toggleNodeClassName, dropdownContentClassName, closeWhenOtherOpened = true, dropdownPlacement = 'bottom-start', containerElement: ContainerElement = 'div', usePortal, portalRoot = document.body, open, onVisibilityChange, popperClassName, ...other })=>{
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        modifiers: popperModifiers,
        placement: dropdownPlacement
    });
    const [contentVisible, setContentVisible] = useControlled({
        controlled: open,
        default: false,
        name: 'FileInput'
    });
    const handleDropdownVisibility = useCallback((e, forceClose)=>{
        e?.preventDefault?.();
        setContentVisible(forceClose ?? not);
        onVisibilityChange?.(forceClose);
    }, [
        setContentVisible,
        onVisibilityChange
    ]);
    const toggleElOnClickFns = useMemo(()=>openOnRightClick ? [
            dispatchCloseOtherDropdownEvent
        ] : [
            handleDropdownVisibility,
            dispatchCloseOtherDropdownEvent
        ], [
        handleDropdownVisibility,
        openOnRightClick
    ]);
    const handleCloseOnContentClick = useCallback(()=>{
        if (closeOnClick) {
            setTimeout(()=>{
                handleDropdownVisibility(undefined, false);
            });
        }
    }, [
        handleDropdownVisibility,
        closeOnClick
    ]);
    const dropdownContent = contentVisible && /*#__PURE__*/ React.createElement("div", {
        className: cx({
            'Dropdown-portal-popper': usePortal
        }, popperClassName),
        ref: setPopperElement,
        style: styles.popper,
        ...attributes.popper
    }, /*#__PURE__*/ React.createElement(DropdownContent, {
        visible: true,
        onClickOutside: when(handleDropdownVisibility, contentVisible),
        onClickCapture: handleCloseOnContentClick,
        onOpenOtherDropdown: handleDropdownVisibility,
        closeWhenOtherOpened: closeWhenOtherOpened,
        className: cx('Dropdown-content--v2', dropdownContentClassName)
    }, children));
    return /*#__PURE__*/ React.createElement(ContainerElement, {
        className: cx('Dropdown', 'Dropdown--v2', className),
        onMouseEnter: openOnHover ? handleDropdownVisibility : null,
        onMouseLeave: openOnHover ? handleDropdownVisibility : null,
        onContextMenu: openOnRightClick ? handleDropdownVisibility : null,
        ...other
    }, /*#__PURE__*/ React.createElement("div", {
        ref: setReferenceElement,
        onClick: chainHandlers(toggleElOnClickFns),
        className: cx('Dropdown-toggle', toggleNodeClassName)
    }, toggleNode), dropdownContent && usePortal ? /*#__PURE__*/ createPortal(dropdownContent, portalRoot) : dropdownContent);
};
Dropdown.propTypes = {
    /**
	 * Children will be rendered within the `Dropdown`
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root
	 */ className: PropTypes.string,
    /**
	 * Dropdown will close itself when clicked somewhere in content
	 */ closeOnClick: PropTypes.bool,
    /**
	 * Dropdown will be closed when other Dropdown is opened. Default true
	 */ closeWhenOtherOpened: PropTypes.bool,
    /**
	 * Element or Component used as the root tag
	 */ containerElement: PropTypes.node,
    /**
	 * Custom `className` for content element
	 */ dropdownContentClassName: PropTypes.string,
    /**
	 * For set popper placement
	 */ dropdownPlacement: PropTypes.string,
    /**
	 * Callback fires when visibility changed
	 */ onVisibilityChange: PropTypes.func,
    /**
	 * controlled variant of open stat
	 */ open: PropTypes.bool,
    /**
	 * If true, dropdown will open on mouse enter and close on mouse leave
	 */ openOnHover: PropTypes.bool,
    /**
	 * If true, dropdown will open on right click
	 */ openOnRightClick: PropTypes.bool,
    /**
	 * Custom `className` for popper
	 */ popperClassName: PropTypes.string,
    /**
	 * Modifiers for popper
	 */ popperModifiers: PropTypes.object,
    /**
	 * Portal root element
	 */ portalRoot: PropTypes.element,
    /**
	 * Custom toggle node for Toggle
	 */ toggleNode: PropTypes.node,
    /**
	 * Custom `className` for toggle node
	 */ toggleNodeClassName: PropTypes.string,
    /**
	 * Render popper in portal or not
	 */ usePortal: PropTypes.bool
};
export default Dropdown;
